// A document's own link in the office, #/doc/<kind>/<id>: an invoice opens on its customer's Money tab
// (a draft at Review, on its job), a payment on its customer's Money tab, a journal entry in Ledger &
// reports, a pay period in Payroll. The address stays while it is open, so a reload or a copied link
// opens it again; the server still decides who may see it.

import { useEffect } from 'react';

export const DOC_KINDS = ['invoice', 'payment', 'journal-entry', 'pay-period'] as const;
export type DocKind = (typeof DOC_KINDS)[number];
export interface DocRef {
  kind: DocKind;
  id: string;
}

export const DOC_LINK =
  /^#\/doc\/(invoice|payment|journal-entry|pay-period)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

export const docHref = (d: DocRef) => `#/doc/${d.kind}/${d.id}`;

export function parseDocLink(hash: string): DocRef | null {
  const m = DOC_LINK.exec(hash);
  return m ? { kind: m[1] as DocKind, id: m[2]!.toLowerCase() } : null;
}

/** What a row a document link opened needs from the page (an HTMLElement). */
export type DocRow = Pick<
  HTMLElement,
  'setAttribute' | 'removeAttribute' | 'hasAttribute' | 'classList' | 'tabIndex' | 'scrollIntoView' | 'focus'
>;

/**
 * Marks the row a document link opened as the current one: scrolled to, focused and aria-current.
 * Returns what undoes it, so opening another document leaves only that one current (QA-557).
 */
export function markDocHere(el: DocRow): () => void {
  const madeFocusable = !el.hasAttribute('tabindex');
  el.setAttribute('aria-current', 'true');
  el.classList.add('doc-here');
  if (madeFocusable) el.tabIndex = -1;
  el.scrollIntoView({ block: 'center' });
  el.focus({ preventScroll: true });
  return () => {
    el.removeAttribute('aria-current');
    el.classList.remove('doc-here');
    if (madeFocusable) el.removeAttribute('tabindex');
  };
}

/**
 * Brings the row a document link opened into view and gives it focus, once it's on screen: the
 * element with data-doc="<id>" is scrolled to, focused and marked current, until another document
 * opens or the screen goes. `ready` says the rows are in.
 */
export function useDocFocus(id: string | null | undefined, ready: boolean): void {
  useEffect(() => {
    if (!id || !ready) return;
    const el = document.querySelector<HTMLElement>(`[data-doc="${CSS.escape(id)}"]`);
    return el ? markDocHere(el) : undefined;
  }, [id, ready]);
}
